/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback } from "react";
import { Modal, Button, Text, Title } from "rizzui";
import {
  BiUpload,
  BiDownload,
  BiCheck,
  BiX,
  BiErrorCircle,
} from "react-icons/bi";
import { useDropzone } from "react-dropzone";
import { IUnit } from "@/utilities/types/Units";
import cn from "@/utils/class-names";

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (units: Partial<IUnit>[]) => Promise<void>;
  existingUnits?: { name: string }[];
}

interface ParsedUnit {
  name: string;
  price: string;
  unitStatus: "available" | "sold" | "not released" | "reserved";
  status: "valid" | "invalid" | "overwrite";
  errors: string[];
}

const TEMPLATE_DATA = [
  { Name: "#10-01", Price: "1500000", Status: "available" },
  { Name: "#10-02", Price: "1600000", Status: "not released" },
  { Name: "#10-03", Price: "1700000", Status: "reserved" },
];

const VALID_STATUSES = ["available", "sold", "not released", "reserved"];

export default function BulkImportModal({
  isOpen,
  onClose,
  onImport,
  existingUnits = [],
}: BulkImportModalProps) {
  const [parsedUnits, setParsedUnits] = useState<ParsedUnit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState("");

  const validateUnit = useCallback(
    (unit: any, index: number, allUnits: any[]): ParsedUnit => {
      const errors: string[] = [];
      let validationStatus: "valid" | "invalid" | "overwrite" = "valid";

      // Validate name
      if (!unit.Name || typeof unit.Name !== "string" || !unit.Name.trim()) {
        errors.push("Name is required");
        validationStatus = "invalid";
      } else {
        const unitName = unit.Name.toString().trim();

        // Check for duplicate names within the CSV
        const duplicateIndex = allUnits.findIndex(
          (u, i) => i !== index && u.Name?.toString().trim() === unitName,
        );
        if (duplicateIndex !== -1) {
          errors.push(`Duplicate name in CSV (row ${duplicateIndex + 2})`);
          validationStatus = "invalid";
        }

        // Check against existing units in database
        const existsInDatabase = existingUnits.some(
          (existingUnit) => existingUnit.name === unitName,
        );
        if (existsInDatabase) {
          errors.push("Unit exists and will be overwritten");
          if (validationStatus === "valid") {
            validationStatus = "overwrite";
          }
        }
      }

      // Validate price
      const price = unit.Price?.toString() || "0";
      if (isNaN(Number(price)) || Number(price) < 0) {
        errors.push("Price must be a valid positive number");
        validationStatus = "invalid";
      }

      // Validate status
      const unitStatus = unit.Status?.toLowerCase() || "available";
      if (!VALID_STATUSES.includes(unitStatus)) {
        errors.push(`Status must be one of: ${VALID_STATUSES.join(", ")}`);
        validationStatus = "invalid";
      }

      return {
        name: unit.Name?.toString().trim() || "",
        price: price,
        unitStatus: unitStatus as any,
        status: validationStatus,
        errors,
      };
    },
    [existingUnits],
  );

  const parseFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvData = e.target?.result as string;

          // Parse CSV manually to preserve exact text values
          const lines = csvData.split("\n").filter((line) => line.trim());
          const headers = lines[0].split(",").map((h) => h.trim());

          const objects = lines.slice(1).map((line) => {
            const values = line
              .split(",")
              .map((v) => v.trim().replace(/^"|"$/g, ""));
            const obj: any = {};

            headers.forEach((header, colIndex) => {
              obj[header] = values[colIndex] || "";
            });

            return obj;
          });

          const parsed = objects.map((row: unknown, index: number) =>
            validateUnit(row, index, objects),
          );
          setParsedUnits(parsed);
          setFileName(file.name);
        } catch (error) {
          console.error("Error parsing file:", error);
          alert("Error parsing file. Please check the format.");
        }
      };
      reader.readAsText(file);
    },
    [validateUnit],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        parseFile(acceptedFiles[0]);
      }
    },
    [parseFile],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const downloadTemplate = () => {
    // Create CSV content
    const headers = ["Name", "Price", "Status"];
    const csvContent = [
      headers.join(","),
      ...TEMPLATE_DATA.map((row) =>
        [row.Name, row.Price, row.Status].join(","),
      ),
    ].join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "units_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    const unitsToImport = parsedUnits
      .filter((unit) => unit.status === "valid" || unit.status === "overwrite")
      .map(
        (unit) =>
          ({
            name: unit.name,
            price: Number(unit.price),
            status: unit.unitStatus,
          }) as IUnit,
      );

    if (unitsToImport.length === 0) {
      alert("No valid units to import");
      return;
    }

    setIsLoading(true);
    try {
      await onImport(unitsToImport);
      onClose();
      setParsedUnits([]);
      setFileName("");
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const validCount = parsedUnits.filter(
    (unit) => unit.status === "valid",
  ).length;
  const invalidCount = parsedUnits.filter(
    (unit) => unit.status === "invalid",
  ).length;
  const overwriteCount = parsedUnits.filter(
    (unit) => unit.status === "overwrite",
  ).length;
  const totalImportCount = validCount + overwriteCount;

  const getRowStyle = (status: ParsedUnit["status"]) => {
    switch (status) {
      case "valid":
        return "bg-green-50";
      case "invalid":
        return "bg-red-50";
      case "overwrite":
        return "bg-yellow-50";
      default:
        return "";
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <div className="p-6">
        <div className="mb-6">
          <Title as="h3" className="text-xl font-semibold mb-2">
            Bulk Insert Units
          </Title>
          <Text className="text-gray-600">
            Insert multiple units from CSV file. Existing units will be
            overwritten.
          </Text>
        </div>

        {/* Template Download */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <Text className="font-medium text-blue-900">
                Download Template
              </Text>
              <Text className="text-sm text-blue-700">
                Use this template to format your data correctly.
              </Text>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              <BiDownload className="mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* File Upload */}
        <div
          {...getRootProps()}
          className={cn(
            "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
            isDragActive
              ? "border-blue-400 bg-blue-50"
              : "border-gray-300 hover:border-gray-400",
          )}
        >
          <input {...getInputProps()} />
          <BiUpload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          {isDragActive ? (
            <Text>Drop the file here...</Text>
          ) : (
            <div>
              <Text className="text-lg font-medium mb-2">
                Drop your file here, or click to select
              </Text>
              <Text className="text-sm text-gray-500">Supports CSV file</Text>
            </div>
          )}
        </div>

        {/* Preview */}
        {parsedUnits.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <Title as="h4" className="text-lg font-medium">
                Preview ({fileName})
              </Title>
              <div className="flex gap-4 text-sm">
                <span className="text-green-600">
                  <BiCheck className="inline mr-1" />
                  New: {validCount}
                </span>
                {overwriteCount > 0 && (
                  <div className="flex items-center">
                    <BiErrorCircle className="inline mr-1 text-yellow-600" />
                    <span className="text-yellow-600">
                      Overwrite: {overwriteCount}
                    </span>
                  </div>
                )}
                {invalidCount > 0 && (
                  <span className="text-red-600">
                    <BiX className="inline mr-1" />
                    Invalid: {invalidCount}
                  </span>
                )}
              </div>
            </div>

            {invalidCount > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Text className="text-red-800 text-sm">
                  <BiX className="inline mr-1" />
                  {invalidCount} units have validation errors. Please fix these
                  issues before importing. Only New and Overwrite units will be
                  imported.
                </Text>
              </div>
            )}

            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left"></th>
                    <th className="px-4 py-2 text-left">Name</th>
                    <th className="px-4 py-2 text-left">Price</th>
                    <th className="px-4 py-2 text-left">Status</th>
                    <th className="px-4 py-2 text-left">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedUnits.map((unit, index) => (
                    <tr
                      key={index}
                      className={cn("border-t", getRowStyle(unit.status))}
                    >
                      <td className="px-4 py-2 text-center">
                        {unit.status === "valid" && (
                          <BiCheck className="text-green-600" />
                        )}
                        {unit.status === "invalid" && (
                          <BiX className="text-red-600" />
                        )}
                        {unit.status === "overwrite" && (
                          <BiErrorCircle className="text-yellow-600" />
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono">{unit.name}</td>
                      <td className="px-4 py-2">{unit.price}</td>
                      <td className="px-4 py-2 capitalize">
                        {unit.unitStatus}
                      </td>
                      <td
                        className={cn(
                          "px-4 py-2 text-xs",
                          unit.status === "invalid" && "text-red-600",
                          unit.status === "overwrite" && "text-yellow-700",
                        )}
                      >
                        {unit.errors.join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {parsedUnits.length > 0 && totalImportCount > 0 && (
            <Button
              onClick={handleImport}
              isLoading={isLoading}
              disabled={totalImportCount === 0}
            >
              Import {totalImportCount} Units
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
